import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { CustomerDecisionEnum } from '@features/profile/components/consent-details-dialog/consent-details-dialog.component';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  ConsentDetailsDialogConfig,
  IConsentDetailsResultMode,
} from '@features/profile/components/consent-details-dialog/consent-details-dialog-config';
import { initOptionsSelectors } from '@store/app/init-options/init-options.selectors';
import { Store } from '@ngrx/store';
import { ByKey } from '@store/_common/models/common.types';
import { BehaviorSubject, MonoTypeOperatorFunction, of, pipe } from 'rxjs';
import { ConsentsService } from '@store/consents/consents.service';
import { ConsentItem } from '@store/consents/models/consent-item';

type ResultPreviewHtmlType = { isFetching: boolean; html: string };

@Component({
  selector: 'app-consents-dialog-form-item',
  templateUrl: './consents-dialog-form-item.component.html',
  styleUrls: ['./consents-dialog-form-item.component.scss'],
})
export class ConsentsDialogFormItemComponent implements OnInit {
  private id$ = new BehaviorSubject<number>(0);
  private isDataFetching$ = new BehaviorSubject<boolean>(false);

  readonly resultModeEnum = IConsentDetailsResultMode;

  formRef: UntypedFormGroup;
  customerDecisionEnum = CustomerDecisionEnum;
  resultPreviewHtmlMap: Record<number, ResultPreviewHtmlType> = {};

  defaultLang$ = this.store$.select(initOptionsSelectors.locationOptions).pipe(map(({ defaultLang }) => defaultLang));
  uiVersion$ = this.store$.select(initOptionsSelectors.uiVersion);
  config$ = this.uiVersion$.pipe(map(ConsentDetailsDialogConfig.build));
  previewHtml$ = this.id$.asObservable().pipe(
    filter(Boolean),
    this.onlyWhenShowHtml(),
    tap(() => this.isDataFetching$.next(true)),
    withLatestFrom(this.defaultLang$),
    switchMap(([id, defaultLang]) => this.consentsService.getConsentCampaignContent(Number(id), defaultLang)),
    catchError(() => of('')),
    tap(() => this.isDataFetching$.next(false))
  );

  @Input() set form(value: UntypedFormGroup) {
    this.formRef = value;
  }

  get controls() {
    return (this.formRef.get('consents') as UntypedFormArray).controls;
  }

  constructor(private store$: Store<ByKey>, private consentsService: ConsentsService) {}

  get consents(): ConsentItem[] {
    return this.formRef.get('consents').getRawValue() as ConsentItem[];
  }

  ngOnInit(): void {
    this.id$.next(this.formRef.getRawValue().id ?? 0);
    this.createResultsPreviewsMap();
  }

  onFetchItemHtml(id: number): void {
    if (this.resultPreviewHtmlMap[id].isFetching) {
      return;
    }
    this.resultPreviewHtmlMap[id].isFetching = true;

    this.defaultLang$
      .pipe(
        take(1),
        switchMap((lang) => this.consentsService.getConsentCampaignConsentContent(id, lang)),
        catchError(() => of(''))
      )
      .subscribe((previewHtml: string) => {
        this.resultPreviewHtmlMap[id].html = previewHtml;
        this.resultPreviewHtmlMap[id].isFetching = false;
      });
  }

  private createResultsPreviewsMap(): void {
    for (const consent of this.consents) {
      this.resultPreviewHtmlMap[consent.id] = { isFetching: false, html: '' };
    }
  }

  private onlyWhenShowHtml(): MonoTypeOperatorFunction<number> {
    return pipe(
      withLatestFrom(this.config$),
      filter(([, config]) => config?.showHtml === true),
      map(([result]) => result)
    );
  }
}
